import { HttpClient, HttpEventType, HttpHeaders } from "@angular/common/http";
import { EventEmitter, Injectable } from "@angular/core";
import { collection, deleteDoc, doc, docData, Firestore, setDoc } from "@angular/fire/firestore";
import { Functions, httpsCallableData } from '@angular/fire/functions';
import { getDownloadURL, ref, Storage } from '@angular/fire/storage';
import { cloneDeep, isMatch } from "lodash-es";
import { firstValueFrom, Observable, Subject } from "rxjs";
import { AcceptanceFlows } from "src/app/flows/acceptance.flows";
import { serverTimestamp } from "src/app/shared-lib/core/db/firestore.models";
import { _PrintFile, CoverType, PrintingGroup } from 'src/app/shared-lib/domains/product';
import { environment } from "src/environments/environment";
import { AuthService } from "../core/auth.service";
import { Finishing, LaminationType, LaminationWeight, PagesPerSheet, PaperWeight, PrintColor, PrintSettings, Settings } from '../shared-lib/core/settings';
import { executeWithRetry } from "./utils/execute-helper";

export interface A2GoogleUploadTask {
  percentageChanges(): Observable<number | undefined>;

  then(
    onFulfilled?: ((a: any) => any) | null,
    onRejected?: ((a: Error) => any) | null
  ): Promise<any>;

  catch(onRejected: (a: Error) => any): Promise<any>;
}

@Injectable({
  providedIn: "root",
})
export class FileService {
  private defaultCollection = "uploads";
  coverTypeChangedEvent = new EventEmitter();
  cancelUploadsSubject = new Subject<void>();
  public cancelUploads$ = this.cancelUploadsSubject.asObservable();
  API_GET_SIGNED_URL = '/api2/uploads/getSignedUrl';
  constructor(
    private db: Firestore,
    private httpClient: HttpClient,
    private storage: Storage,
    private functions: Functions,
    private acceptanceFlows: AcceptanceFlows,
    private authService: AuthService
  ) {

  }

  createId() {
    return doc(collection(this.db, '__ids')).id;
  }

  addTemporary(id, data, path = this.defaultCollection): Promise<any> {
    data.updatedAt = serverTimestamp();
    data.createdAt = serverTimestamp();
    return setDoc(doc(this.db, `${path}/${id}`), data);
  }

  getTemporaryFile(id, path = this.defaultCollection): Observable<_PrintFile> {
    return docData(doc(this.db, `${path}/${id}`), { idField: "id" }) as Observable<_PrintFile>;
  }

  delTemporary(id, path = this.defaultCollection): Promise<any> {
    return deleteDoc(doc(this.db, `${path}/${id}`));
  }

  private coverSettings = [
    {
      value: {
        id: CoverType.Color2Side1Page,
        coverColor: true,
        twoSided: true,
        pagesPerSheet: PagesPerSheet.ONE
      },
      title: 'Portada a color, doble cara',
      img: 'assets/images/coverType/Color2Side1Page.png',
      video: '',
      requirements: [
        { docColor: false, twoSided: true, pagesPerSheet: PagesPerSheet.ONE },
        { docColor: true, twoSided: true, pagesPerSheet: PagesPerSheet.ONE },
      ]
    },

    {
      value: {
        id: CoverType.Color2Side2Page,
        coverColor: true,
        twoSided: true,
        pagesPerSheet: PagesPerSheet.TWO
      },
      title: 'Portada a color, 2 pág por cara, doble cara',
      img: 'assets/images/coverType/Color2Side2Page.png',
      video: '',
      requirements: [
        { docColor: true, twoSided: true, pagesPerSheet: PagesPerSheet.TWO },
      ]
    },
    {
      value: {
        id: CoverType.Color2Side4Page,
        coverColor: true,
        twoSided: true,
        pagesPerSheet: PagesPerSheet.FOUR
      },
      title: 'Portada a color, 4 pág por cara, doble cara',
      img: 'assets/images/coverType/Color2Side4Page.png',
      video: '',
      requirements: [
        { docColor: true, twoSided: true, pagesPerSheet: PagesPerSheet.FOUR },
      ]
    },
    {
      value: {
        id: CoverType.Color1Side1Page,
        coverColor: true,
        twoSided: false,
        pagesPerSheet: PagesPerSheet.ONE
      },
      title: 'Portada a color',
      img: 'assets/images/coverType/Color1Side1Page.png',
      video: '',
      requirements: [
        { docColor: false, twoSided: false, pagesPerSheet: PagesPerSheet.ONE },
        { docColor: false, twoSided: false, pagesPerSheet: PagesPerSheet.TWO },
        { docColor: false, twoSided: false, pagesPerSheet: PagesPerSheet.FOUR },
        { docColor: false, twoSided: true, pagesPerSheet: PagesPerSheet.ONE },
        { docColor: false, twoSided: true, pagesPerSheet: PagesPerSheet.TWO },
        { docColor: false, twoSided: true, pagesPerSheet: PagesPerSheet.FOUR },
        { docColor: true, twoSided: false, pagesPerSheet: PagesPerSheet.ONE },
        { docColor: true, twoSided: false, pagesPerSheet: PagesPerSheet.TWO },
        { docColor: true, twoSided: false, pagesPerSheet: PagesPerSheet.FOUR },
        { docColor: true, twoSided: true, pagesPerSheet: PagesPerSheet.ONE },
        { docColor: true, twoSided: true, pagesPerSheet: PagesPerSheet.TWO },
        { docColor: true, twoSided: true, pagesPerSheet: PagesPerSheet.FOUR }
      ]
    },
    {
      value: {
        id: CoverType.Color1Side2Page,
        coverColor: true,
        twoSided: false,
        pagesPerSheet: PagesPerSheet.TWO
      },
      title: 'Portada a color, 2 pág por cara',
      img: 'assets/images/coverType/Color1Side2Page.png',
      video: '',
      requirements: [
        { docColor: true, twoSided: false, pagesPerSheet: PagesPerSheet.TWO },
      ]
    },
    {
      value: {
        id: CoverType.Color1Side4Page,
        coverColor: true,
        twoSided: false,
        pagesPerSheet: PagesPerSheet.FOUR
      },
      title: 'Portada a color, 4 pág por cara',
      img: 'assets/images/coverType/Color1Side4Page.png',
      video: '',
      requirements: [
        { docColor: true, twoSided: false, pagesPerSheet: PagesPerSheet.FOUR },
      ]
    },
    {
      value: {
        id: CoverType.Black1Side1Page,
        coverColor: false,
        twoSided: false,
        pagesPerSheet: PagesPerSheet.ONE
      },
      title: 'Portada en blanco y negro',
      img: 'assets/images/coverType/Black1Side1Page.png',
      video: '',
      requirements: [
        { docColor: false, twoSided: false, pagesPerSheet: PagesPerSheet.ONE },
        { docColor: false, twoSided: false, pagesPerSheet: PagesPerSheet.TWO },
        { docColor: false, twoSided: false, pagesPerSheet: PagesPerSheet.FOUR },
        { docColor: false, twoSided: true, pagesPerSheet: PagesPerSheet.ONE },
        { docColor: false, twoSided: true, pagesPerSheet: PagesPerSheet.TWO },
        { docColor: false, twoSided: true, pagesPerSheet: PagesPerSheet.FOUR },
      ]
    },
    {
      value: {
        id: CoverType.Black1Side2Page,
        coverColor: false,
        twoSided: false,
        pagesPerSheet: PagesPerSheet.TWO
      },
      title: 'Portada en blanco y negro, 2 pág. por cara',
      img: 'assets/images/coverType/Black1Side2Page.png',
      video: '',
      requirements: [
        { docColor: false, twoSided: false, pagesPerSheet: PagesPerSheet.TWO },
      ]
    },
    {
      value: {
        id: CoverType.Black1Side4Page,
        coverColor: false,
        twoSided: false,
        pagesPerSheet: PagesPerSheet.FOUR
      },
      title: 'Portada en blanco y negro, 4 pág. por cara',
      img: 'assets/images/coverType/Black1Side4Page.png',
      video: '',
      requirements: [
        { docColor: false, twoSided: false, pagesPerSheet: PagesPerSheet.FOUR },
      ]
    },
    {
      value: {
        id: CoverType.Black2Side1Page,
        coverColor: false,
        twoSided: true,
        pagesPerSheet: PagesPerSheet.ONE
      },
      title: 'Portada en blanco y negro, doble cara',
      img: 'assets/images/coverType/Black2Side1Page.png',
      video: '',
      requirements: [
        { docColor: false, twoSided: true, pagesPerSheet: PagesPerSheet.ONE },
      ]
    },
    {
      value: {
        id: CoverType.Black2Side2Page,
        coverColor: false,
        twoSided: true,
        pagesPerSheet: PagesPerSheet.TWO
      },
      title: 'Portada en blanco y negro, 2 pág por cara, doble cara',
      img: 'assets/images/coverType/Black2Side2Page.png',
      video: '',
      requirements: [
        { docColor: false, twoSided: true, pagesPerSheet: PagesPerSheet.TWO },
      ]
    },
    {
      value: {
        id: CoverType.Black2Side4Page,
        coverColor: false,
        twoSided: true,
        pagesPerSheet: PagesPerSheet.FOUR
      },
      title: 'Portada en blanco y negro, 4 pág por cara, doble cara',
      img: 'assets/images/coverType/Black2Side4Page.png',
      video: '',
      requirements: [
        { docColor: false, twoSided: true, pagesPerSheet: PagesPerSheet.FOUR },
      ]
    },
  ];

  laminationOptions = {
    [LaminationType.Matte]: {
      true: [
        {
          title: 'Flexible',
          value: LaminationWeight.W125
        },
        {
          title: 'Estándar',
          value: LaminationWeight.W150
        },
        {
          title: 'Premium',
          value: LaminationWeight.W250
        }
      ],
      false: [
        {
          title: 'Flexible',
          value: LaminationWeight.W125
        },
        {
          title: 'Estándar',
          value: LaminationWeight.W150
        },
        {
          title: 'Grueso',
          value: LaminationWeight.W250
        }
      ]
    },
    [LaminationType.Gloss]: {
      true: [
        {
          title: 'Flexible',
          value: LaminationWeight.W80
        },
        {
          title: 'Estándar',
          value: LaminationWeight.W125
        },
        {
          title: 'Grueso',
          value: LaminationWeight.W175
        },
        {
          title: 'Premium',
          value: LaminationWeight.W250
        }
      ],
      false: [
        {
          title: 'Flexible',
          value: LaminationWeight.W80
        },
        {
          title: 'Estándar',
          value: LaminationWeight.W125
        },
        {
          title: 'Grueso',
          value: LaminationWeight.W175
        },
        {
          title: 'Extra grueso',
          value: LaminationWeight.W250
        }
      ]
    }
  };

  getCoverSettings() {
    return cloneDeep(this.coverSettings);
  }

  // TODO: To helper (dont know which one)
  getDefaultCoverType(pFile, printSettings: PrintSettings) {
    const coverColor = pFile.docColor || pFile.coverLaminated;
    const twoSided = pFile.coverLaminated ? false : printSettings[Settings.TWO_SIDED];
    const pagesPerSheet = pFile.coverLaminated ? PagesPerSheet.ONE : printSettings[Settings.PAGES_PER_SHEET];
    const paperWeight = (pFile.coverType?.paperWeight && printSettings[Settings.FINISHING] !== Finishing.LAMINATED) ? pFile.coverType?.paperWeight : printSettings[Settings.PAPER_WEIGHT];
    return {
      id: `${coverColor ? 'Color' : 'Black'}${twoSided ? '2Side' : '1Side'}${pagesPerSheet}Page`,
      coverColor,
      twoSided,
      pagesPerSheet,
      paperWeight,
      default: true
    };
  }

  // TODO: To helper (dont know which one)
  getCoverTypeImage(coverSettings, generalSettings) {
    const result = coverSettings.id + '-' +
      (generalSettings.docColor ? 'Color' : 'Black') +
      (generalSettings.twoSided ? '2' : '1') + 'Side' +
      generalSettings.pagesPerSheet + 'Page.png';
    return `assets/images/coverType/${result}`;
  }

  restartCoverTypes(printingGroup, i = null) {
    if (i !== null) {
      const file = printingGroup.files[i];
      file.coverType = {
        ...this.getDefaultCoverType(file, printingGroup.printSettings),
        default: true,
        ...(file.coverType?.paperWeightChanged !== undefined && { paperWeightChanged: file.coverType.paperWeightChanged })

      };
      this.coverTypeChangedEvent.emit(i);
    } else {
      printingGroup.files.forEach((file, index) => {
        file.coverType = {
          ...this.getDefaultCoverType(file, printingGroup.printSettings),
          default: true,
          ...(file.coverType.paperWeightChanged !== undefined && { paperWeightChanged: file.coverType.paperWeightChanged })

        };
        this.coverTypeChangedEvent.emit(index);
      });
    }

  }

  checkCoverTypesWeights(printingGroup: PrintingGroup) {
    printingGroup.files.forEach((file, index) => {

      if (Number(printingGroup.printSettings[Settings.PAPER_WEIGHT]) >= Number(file.coverType.paperWeight)) {
        file.coverType.paperWeight = printingGroup.printSettings[Settings.PAPER_WEIGHT];
      } else {
        file.coverType.paperWeight = file.coverType.paperWeightChanged ?? printingGroup.printSettings[Settings.PAPER_WEIGHT];
      }
      if (file.coverLaminated) {
        file.coverType.paperWeight = PaperWeight.W80;
      }
    });

  }

  getLaminationTypeName(type) {
    switch (type) {
      case LaminationType.Matte:
        return 'Mate';
      case LaminationType.Gloss:
        return 'Brillo';
      default:
        return '';
    }
  }

  getLaminationWeightName(option, selectedType, coverLaminated = false, isBindingLaminated = false) {
    let name = this.laminationOptions[selectedType][coverLaminated].find(el => el.value === option)?.title;
    if (['Grueso', 'Extra grueso'].includes(name) && isBindingLaminated) {
      name = 'Premium';
    }
    return name;
  }

  processFile(id, printSettings, collection = "uploads"): Promise<any> {
    if (!(id && printSettings)) {
      return Promise.reject();
    }
    const data = {
      collection,
      uploadId: id,
      printSettings: printSettings,
    };

    if (this.acceptanceFlows.getPrintAllowed()) {
      data["printAllowed"] = true;
    }
    const _fn = (data) =>
      firstValueFrom(httpsCallableData(this.functions, `api2files/uploads/process/${data.uploadId}`)(data));
    return executeWithRetry(_fn, { data, maxCount: 3, self: this }).catch(
      (err) => {
        console.error("--- ProcessFile Process ---");
        console.error(err);
        return err;
      }
    );
  }

  updateThumbnail(
    id,
    printSettings,
    collection = "uploads",
    skipProductUpdate = false
  ): Promise<any> {
    const data = {
      collection,
      uploadId: id,
      printSettings: printSettings,
      skipProductUpdate,
    };
    const _fn = (data) =>
      firstValueFrom(httpsCallableData(this.functions, `api2files/uploads/thumbnail/${data.uploadId}`)(data));
    return executeWithRetry(_fn, { data, maxCount: 3, self: this }).catch(
      (err) => {
        console.error("--- ProcessFile thumbnail ---");
        console.error(err);
        return err;
      }
    );
  }

  //Usado por file Component
  getThumbFile(fileUploaded: _PrintFile, settings: PrintSettings, pFile = null) {
    const _settings = cloneDeep(settings);
    _settings.color = _settings.docColor || _settings.coverColor ? PrintColor.COLOR : PrintColor.BLACKNWHITE;
    _settings.pagesPerSheet = pFile.coverColor && !pFile.docColor ? 1 : settings.pagesPerSheet;
    delete _settings.coverColor;
    fileUploaded.thumbs?.forEach(th => {
      delete th.printSettings.coverColor;
    });
    if (pFile.coverType) {
      _settings.coverColor = pFile.coverType.coverColor;
      _settings.pagesPerSheet = pFile.coverType.pagesPerSheet;
    }
    const thumb = fileUploaded?.thumbs?.find((thumb) =>
      isMatch(_settings, thumb.printSettings)
    );
    return thumb ? thumb.file : null;
  }

  async getSignedUrl(path, contentType): Promise<any> {
    const headers = await this._getHttpHeaders();
    if (headers) {
      const body = { path, contentType };
      return firstValueFrom(httpsCallableData(this.functions, `${this.API_GET_SIGNED_URL}`)(body));
    } else {
      return Promise.reject(new Error("fail-invalid currentUser"));
    }
  }

  upload(path, file): A2GoogleUploadTask {
    const percentage = new Subject();
    let subscription;

    const promise = new Promise((resolve, reject): any => {
      this.getSignedUrl(path, file.type).then((r) => {
        const signedUrl = r.signedUrl;
        const httpOptions = {
          headers: new HttpHeaders(r.headers),
          observe: "events",
          reportProgress: true,
        } as any;

        subscription = this.httpClient
          .put(signedUrl, file, httpOptions)
          .subscribe(
            (event: any) => {
              if (event.type === HttpEventType.UploadProgress) {
                percentage.next((event.loaded / event.total) * 100);
              }
            },
            (error) => reject(error),
            () => {
              //Complete
              resolve({
                ref: {
                  getDownloadURL: () =>
                    getDownloadURL(ref(this.storage, path)),
                },
              });
            }
          );
      });
    }) as any;

    promise.percentageChanges = () => percentage;
    promise.subscription = () => subscription;
    return promise;
  }

  async _getHttpHeaders() {
    const idToken = await this.authService.getIdToken();
    if (idToken) {
      return new HttpHeaders({
        Authorization: `Bearer ${idToken}`,
        "project-id": `${environment.firebaseConfig.projectId}`,
      });
    } else {
      return null;
    }
  }

  public triggerCancelUploads(): void {
    this.cancelUploadsSubject.next();
  }
}

